import { Sparkles, BarChart3, FileCheck, RefreshCw } from "lucide-react";
import Reveal from "./Reveal";
import PreviewAudit from "./PreviewAudit";

const STEPS_AFTER = [
  {
    id: 2,
    icon: BarChart3,
    title: "See your risk score.",
    body: "Get your compliance score (0–100), your inventory of AI systems, and a list of what's missing. Most SMEs find 3–7 AI systems they didn't know were regulated.",
  },
  {
    id: 3,
    icon: FileCheck,
    title: "Generate audit-ready documentation.",
    body: "Subscribe (€299/month) and unlock the full Compliance Pack: AI inventory register, risk assessments, transparency notices, governance policy, employee guidelines.",
  },
  {
    id: 4,
    icon: RefreshCw,
    title: "Stay compliant as the rules change.",
    body: "Quarterly regulatory updates. New EU guidance auto-flagged. New AI tools onboarded into your inventory in 2 minutes.",
  },
];

const StepNumber = ({ n, accent = false, big = false }) => (
  <span
    className={`tabular-nums font-semibold tracking-[0.18em] ${big ? "text-[14px]" : "text-[12px]"}`}
    style={{ color: accent ? "var(--blue)" : "var(--muted-2)" }}
  >
    {String(n).padStart(2, "0")}
  </span>
);

const StepOne = () => (
  <Reveal>
    <article
      data-testid="how-step-1"
      className="how-step-1 relative overflow-hidden rounded-2xl border bg-white p-7 md:p-10"
      style={{ borderColor: "var(--ink)" }}
    >
      <div
        aria-hidden="true"
        className="absolute -right-12 -top-12 h-44 w-44 rounded-full"
        style={{ background: "var(--blue-50)" }}
      />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              className="inline-flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "var(--ink)", color: "#ffffff" }}
            >
              <Sparkles className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
            </span>
            <StepNumber n={1} accent big />
          </div>
          <span
            data-testid="how-step-1-badge"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold tracking-[0.18em] uppercase"
            style={{ background: "var(--blue)", color: "#ffffff" }}
          >
            Free · 5 min
          </span>
        </div>

        <h3
          data-testid="how-step-1-title"
          className="display-h2 balance mt-7 text-3xl sm:text-4xl md:text-[44px]"
          style={{ color: "var(--ink)" }}
        >
          Take the audit. Free. Five minutes.
        </h3>

        <p
          data-testid="how-step-1-body"
          className="pretty mt-5 max-w-2xl text-[16.5px] leading-relaxed md:text-lg"
          style={{ color: "var(--muted)" }}
        >
          Answer 20 questions about how your team uses AI. We map every tool to AI Act risk tiers — Annex III high-risk,
          transparency-required, or no obligation.
        </p>
      </div>
    </article>
  </Reveal>
);

const StepCardSmall = ({ step, index }) => {
  const Icon = step.icon;
  return (
    <Reveal delay={index * 80}>
      <article
        data-testid={`how-step-${step.id}`}
        className="step-card relative flex h-full flex-col rounded-2xl border bg-white p-6 md:p-7"
        style={{ borderColor: "var(--line)" }}
      >
        <div className="flex items-center justify-between">
          <span
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ background: "var(--blue-50)", color: "var(--blue)" }}
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden="true" />
          </span>
          <StepNumber n={step.id} />
        </div>
        <h3
          data-testid={`how-step-${step.id}-title`}
          className="mt-6 text-[18px] font-semibold tracking-tight leading-snug"
          style={{ color: "var(--ink)" }}
        >
          {step.title}
        </h3>
        <p
          data-testid={`how-step-${step.id}-body`}
          className="mt-3 text-[14.5px] leading-relaxed"
          style={{ color: "var(--muted)" }}
        >
          {step.body}
        </p>
      </article>
    </Reveal>
  );
};

export const HowItWorks = () => (
  <section
    id="how"
    data-testid="section-how"
    className="border-t"
    style={{ borderColor: "var(--line)", background: "var(--tint)" }}
  >
    <div className="mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-28">
      <div className="grid gap-10 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-4">
          <Reveal>
            <div className="sec-num" data-testid="how-eyebrow">
              <span>02 — How it works</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h2
              data-testid="how-headline"
              className="display-h2 balance mt-5 text-3xl sm:text-4xl md:text-[44px]"
              style={{ color: "var(--ink)" }}
            >
              Free audit first. Subscribe only if you need the documents.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-5 max-w-sm text-[15px] leading-relaxed" style={{ color: "var(--muted)" }}>
              Four steps from "I have no idea what we're exposed to" to audit-ready before the August 2, 2026 deadline.
            </p>
          </Reveal>
        </div>

        <div className="md:col-span-8">
          <StepOne />

          <PreviewAudit />

          <div className="connector" aria-hidden="true" />

          <div className="mt-12 grid gap-5 sm:grid-cols-1 lg:grid-cols-3" data-testid="how-grid">
            {STEPS_AFTER.map((s, i) => (
              <StepCardSmall key={s.id} step={s} index={i} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default HowItWorks;
